import { Route, Routes } from '@angular/router';

import { RouteTranslatorService } from './route-translator.service';

/**
 * Module-scope translator reference.
 *
 * Lazy child route configs are built with `RouterModule.forChild(localizeRoutes(routes))`,
 * which is evaluated when the lazy chunk is first imported — always after
 * `APP_INITIALIZER` has run, so the translator is guaranteed to be registered by then.
 * A singleton avoids threading DI through every child routing module.
 */
let translator: RouteTranslatorService | null = null;

export function registerRouteTranslator(instance: RouteTranslatorService): void {
  translator = instance;
}

/** Test seam: drop the registered translator. */
export function resetRouteTranslator(): void {
  translator = null;
}

/**
 * Translate a route tree in place and return it.
 *
 * Use for the root config (via `Router.resetConfig`) and for every lazy child config that
 * contains `ROUTES.*` keys — currently the auth, edit-profile and dashboard modules.
 *
 * Translation is idempotent: the untranslated value is stashed under
 * `route.data.localizeRouter` on first pass and re-read on subsequent passes, so calling
 * this twice cannot double-translate.
 */
export function localizeRoutes(routes: Routes): Routes {
  if (!translator) {
    // Failing loudly matters: silently skipping translation would ship untranslated,
    // SEO-visible URLs such as /Misc_tutor_search instead of /docenten.
    throw new Error(
      'localizeRoutes() called before the route translator was registered. ' +
      'Ensure the localize APP_INITIALIZER has completed before any route config is built.'
    );
  }
  translateRouteTree(routes, translator, true);
  return routes;
}

function translateRouteTree(routes: Routes, service: RouteTranslatorService, isRootTree = false): void {
  for (const route of routes) {
    const skipRouteLocalization = route.data?.['skipRouteLocalization'];
    const localizeRedirection = !skipRouteLocalization || skipRouteLocalization['localizeRedirectTo'];

    if (route.redirectTo && localizeRedirection && typeof route.redirectTo !== 'function') {
      const prefixLang = route.redirectTo.indexOf('/') === 0 || isRootTree;
      translateProperty(route, 'redirectTo', service, prefixLang);
    }

    if (skipRouteLocalization) {
      continue;
    }

    if (route.path !== null && route.path !== undefined) {
      translateProperty(route, 'path', service, false);
    }

    if (route.children) {
      translateRouteTree(route.children, service);
    }
  }
}

function translateProperty(
  route: Route,
  property: 'path' | 'redirectTo',
  service: RouteTranslatorService,
  prefixLang: boolean
): void {
  const data: Record<string, any> = (route.data = route.data ?? {});
  if (!data['localizeRouter']) {
    data['localizeRouter'] = {};
  }
  if (!data['localizeRouter'][property]) {
    data['localizeRouter'] = { ...data['localizeRouter'], [property]: route[property] };
  }

  const result = service.translatePath(data['localizeRouter'][property]);
  route[property] = prefixLang ? service.stripTrailingSlash(result) : result;
}
